import React, { memo, useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'

import { useDispatch } from 'react-redux'
import { useGoogleLogin } from '@react-oauth/google'

import { useMarket } from 'src/hooks/useMarket'
import { useSystem } from 'src/hooks/useSystem'
import { loginGoogleRequestAction } from 'client/contexts/Login/actions'

import { GoogleLoginButtonBase, CustomButton } from './styles'
import messages from './messages'
import IconGoogle from 'client/icons/Google'
import Cookies from 'js-cookie'
import { COOKIE_GOOGLE_ACCESS_TOKEN } from 'src/constants/cookies'

interface Props {
  disabled?: boolean
  from?: string
}

const GoogleLoginButton = ({ disabled = false, from = '', ...props }: Props) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const { market, language } = useMarket()
  const { apiHostName } = useSystem()

  const handleSuccessLogin = useCallback(
    async (data: { id_token: string; access_token: string }) => {
      if (!data.id_token) {
        return
      }

      Cookies.set(COOKIE_GOOGLE_ACCESS_TOKEN, data.access_token)

      dispatch(
        loginGoogleRequestAction({
          hostname: apiHostName,
          data: { token: data.id_token, ...data, from },
          market,
          language,
        })
      )
    },
    [dispatch, apiHostName, from, language, market]
  )

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      ;(async () => {
        try {
          const { data, type } = JSON.parse(event.data)
          if (type === 'NATIVE_GOOGLE_LOGIN_SUCCESS') {
            await handleSuccessLogin({
              ...data,
              access_token: data.serverAuthCode,
              id_token: data.idToken,
            })
          }
        } catch (error) {
          console.warn('Error handling message:', (error as Error).message)
        }
      })()
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleSuccessLogin])

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: ({ code }) => {
      if (!code) {
        return
      }

      fetch('/api/auth/google', {
        method: 'POST',
        body: JSON.stringify({ code }),
      })
        .then(response => response.json())
        .then(data => handleSuccessLogin(data))
    },
  })

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      googleLogin?.()

      if (typeof window !== 'undefined' && 'ReactNativeWebView' in window) {
        const reactNativeWebView = window.ReactNativeWebView as {
          postMessage: (message: string) => void
        }
        reactNativeWebView.postMessage(
          JSON.stringify({
            type: 'GOOGLE_LOGIN_CLICK',
            action: 'click',
          })
        )
      }
    },
    [googleLogin]
  )

  return (
    <GoogleLoginButtonBase>
      <CustomButton onClick={handleClick} disabled={disabled} {...props}>
        <div>
          <IconGoogle />
        </div>
        <span>{intl.formatMessage(messages.signInWithGoogle)}</span>
      </CustomButton>
    </GoogleLoginButtonBase>
  )
}

export default memo(GoogleLoginButton)
